import React, { useEffect } from "react";
import useSidebar from "zustand-store/sidebar";
import { Link, useLocation } from "react-router-dom";
import { PanelLeftClose, PanelLeftOpen } from "lucide-react";
import { NON_DASHBOARD_PAGES, SIDEBAR_ITEMS } from "enums";
import ProfileDropdown from "./profile-dropdown";
import { cn } from "components/ui/cn";

/* Desktop rail, on the brand's own dark ground (`footer-ground`, standards §4)
   with the mandatory grain overlay (§5).

   It has been grey and it has been tinted; both read as "no sidebar at all"
   against a white workspace, and the owner said so twice. A dark rail is what
   anchors the left edge and gives the screen a place for the eye to land before
   it moves to the white canvas — and it is a treatment the brand already owns,
   rather than a new one invented for the app.

   `footer-ground` flat, not the `deep` gradient: that gradient starts at
   #216351, which is exactly the brand mark's own green, so the mark's tile would
   dissolve into the top of the rail.

   §8 governs the lockup here — its wordmark is dark and would disappear, so the
   rail pairs the mark with the wordmark set live in the display face. An
   exported reversed lockup would be better; raised for the brand owner.

   On this ground type goes white, rules go rgba(255,255,255,.12) per `.is-ondeep`,
   and `accent-light` carries the active state — `accent` itself is too close to
   the ground to read. */

const NavItem = ({ item, isOpen, isActive }) => (
  <li>
    <Link
      to={item.to}
      aria-current={isActive ? "page" : undefined}
      title={!isOpen ? item.label : undefined}
      className={cn(
        "group relative flex items-center gap-3 py-3 border-l-[3px] border-solid",
        "transition-colors duration-ui ease-ui",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent-light",
        isOpen ? "pl-6 pr-4" : "justify-center",
        isActive
          ? "border-accent-light bg-white/12 text-white"
          : "border-transparent text-white/70 hover:bg-white/8 hover:text-white"
      )}
    >
      <span className="flex-shrink-0">{item.icon}</span>
      <span
        className={cn(
          "font-body text-ui-sm overflow-hidden whitespace-nowrap transition-all duration-ui ease-ui",
          isActive && "font-medium",
          isOpen ? "w-auto opacity-100" : "w-0 opacity-0"
        )}
      >
        {item.label}
      </span>

      {/* Blinking accent dot — the brand's link signature, held to the rail's
          width so nothing reflows. Never animate padding for this. */}
      {isOpen && (
        <span
          aria-hidden="true"
          className={cn(
            "ml-auto h-1.5 w-1.5 rounded-full bg-accent-light transition-opacity duration-ui ease-ui",
            isActive ? "opacity-100 animate-dot-blink" : "opacity-0 group-hover:opacity-100"
          )}
        />
      )}
    </Link>
  </li>
);

const Sidebar = () => {
  const location = useLocation();
  const { isOpen, setIsOpen } = useSidebar();

  const hideMenu = NON_DASHBOARD_PAGES.includes(location.pathname);
  const paymentStatusPage = ["/payment"].includes(location.pathname);

  useEffect(() => {
    if (hideMenu) {
      setIsOpen(true);
    }
  }, [hideMenu, setIsOpen]);

  if (paymentStatusPage || hideMenu) {
    return <></>;
  }

  return (
    <div className="hidden lg:block">
      <div
        className={cn(
          "fixed top-0 left-0 z-20 flex h-screen max-h-screen flex-col pt-5",
          "bg-footer-ground",
          "transition-all duration-200",
          isOpen ? "w-[240px]" : "w-[80px]"
        )}
      >
        {/* Grain. Not decoration — it is what stops the flat green banding on a
            surface this large. Standards §5. */}
        <span
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 bg-grain mix-blend-overlay"
          style={{ opacity: "var(--ns-grain-opacity)" }}
        />

        {/* Logo and toggle share one row so neither can overlap the other. */}
        <div
          className={cn(
            "relative mb-8 flex flex-shrink-0 items-center",
            // Expanded: h-10 with the rail's pt-5 centres the lockup at 40px,
            // matching the page title in components/page-header.jsx. Collapsed
            // there is no title to line up with, and the 80px rail cannot hold
            // the mark and the toggle side by side, so they stack.
            isOpen ? "h-10 justify-between gap-2 pl-5 pr-3" : "flex-col gap-4"
          )}
        >
          <Link
            to="/"
            aria-label="NirvaScribe home"
            className={cn("flex items-center gap-2", !isOpen && "justify-center")}
          >
            <img alt="" src="/brand-mark.png" className={isOpen ? "h-7 w-7" : "h-8 w-8"} />
            {isOpen && (
              <span className="font-display text-ui-md font-semibold tracking-[-0.01em] text-white">
                NirvaScribe
              </span>
            )}
          </Link>

          <button
            onClick={() => setIsOpen(!isOpen)}
            aria-label={isOpen ? "Collapse sidebar" : "Expand sidebar"}
            className={cn(
              "flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-ns-sm",
              "text-white/70 transition-colors duration-ui ease-ui hover:bg-white/12 hover:text-white",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-light"
            )}
          >
            {isOpen ? <PanelLeftClose size={16} /> : <PanelLeftOpen size={16} />}
          </button>
        </div>

        <div className="scrollbar-thin relative flex-1 overflow-y-auto">
          <nav>
            <ul className="space-y-0.5">
              {SIDEBAR_ITEMS(isOpen).map((item) => (
                <NavItem
                  key={item.to}
                  item={item}
                  isOpen={isOpen}
                  isActive={location.pathname === item.to}
                />
              ))}
            </ul>
          </nav>
        </div>

        {isOpen && (
          <div className="relative flex-shrink-0 border-t border-solid border-white/12">
            <div className="px-3 py-3">
              <ProfileDropdown />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Sidebar;
